import { useState, useMemo, useRef, useCallback } from 'react'
import { useLanguage } from '@/i18n/LanguageContext'
import { toast } from 'sonner'
import { Upload, Search, X, AlertCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import {
  DndContext,
  DragOverlay,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
  useDraggable,
  type DragStartEvent,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  useFbGroups,
  useMapAccounts,
  useMapSyncGroups,
  useImportStructured,
  type MapAccount,
} from '@/api/hooks/useExtension'
import type { FbGroup } from '@/types'

// ─── Constants ──────────────────────────────────────────────

const COLUMN_COLORS = [
  { bg: 'rgba(239,68,68,0.05)', border: 'rgba(239,68,68,0.2)', text: '#ef4444' },
  { bg: 'rgba(168,85,247,0.05)', border: 'rgba(168,85,247,0.2)', text: '#a855f7' },
  { bg: 'rgba(59,130,246,0.05)', border: 'rgba(59,130,246,0.2)', text: '#3b82f6' },
  { bg: 'rgba(234,179,8,0.05)', border: 'rgba(234,179,8,0.2)', text: '#eab308' },
  { bg: 'rgba(20,184,166,0.05)', border: 'rgba(20,184,166,0.2)', text: '#14b8a6' },
  { bg: 'rgba(249,115,22,0.05)', border: 'rgba(249,115,22,0.2)', text: '#f97316' },
  { bg: 'rgba(236,72,153,0.05)', border: 'rgba(236,72,153,0.2)', text: '#ec4899' },
  { bg: 'rgba(34,197,94,0.05)', border: 'rgba(34,197,94,0.2)', text: '#22c55e' },
  { bg: 'rgba(99,102,241,0.05)', border: 'rgba(99,102,241,0.2)', text: '#6366f1' },
  { bg: 'rgba(244,63,94,0.05)', border: 'rgba(244,63,94,0.2)', text: '#f43f5e' },
  { bg: 'rgba(14,165,233,0.05)', border: 'rgba(14,165,233,0.2)', text: '#0ea5e9' },
  { bg: 'rgba(163,230,53,0.05)', border: 'rgba(163,230,53,0.2)', text: '#a3e635' },
]

// ─── Parser ─────────────────────────────────────────────────

interface ParsedCategory {
  name: string
  urls: string[]
  pageId: string | null
}

function parseStructuredList(text: string): ParsedCategory[] {
  const lines = text.split('\n')
  const categories: ParsedCategory[] = []
  let current: ParsedCategory | null = null

  for (const raw of lines) {
    const line = raw.trim()
    if (!line) continue

    if (line.includes('facebook.com/groups/')) {
      if (current) {
        if (!current.urls.includes(line)) {
          current.urls.push(line)
        }
      }
    } else {
      current = { name: line, urls: [], pageId: null }
      categories.push(current)
    }
  }

  return categories
}

function fuzzyMatch(a: string, b: string): boolean {
  const normalize = (s: string) => s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]/g, '')
  const na = normalize(a)
  const nb = normalize(b)
  return na.includes(nb) || nb.includes(na)
}

// ─── DnD Wrappers ───────────────────────────────────────────

function DroppableColumn({ id, children, className, style }: {
  id: string
  children: React.ReactNode
  className?: string
  style?: React.CSSProperties
}) {
  const { setNodeRef, isOver } = useDroppable({ id })
  return (
    <div
      ref={setNodeRef}
      className={className}
      style={{
        ...style,
        outline: isOver ? '2px solid rgba(99,102,241,0.5)' : undefined,
        outlineOffset: '-2px',
        transition: 'outline 150ms ease',
      }}
    >
      {children}
    </div>
  )
}

function DraggableGroupCard({ group, columnId, linkCount, onRemove, onDoubleClick }: {
  group: FbGroup
  columnId: string
  linkCount: number
  onRemove?: () => void
  onDoubleClick?: () => void
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `${columnId}::${group.id}`,
    data: { group, sourceColumnId: columnId },
  })

  return (
    <div ref={setNodeRef} {...listeners} {...attributes} style={{ opacity: isDragging ? 0.4 : 1 }}>
      <GroupCard group={group} linkCount={linkCount} onRemove={onRemove} onDoubleClick={onDoubleClick} />
    </div>
  )
}

// ─── GroupCard ───────────────────────────────────────────────

function GroupCard({ group, linkCount, onRemove, onDoubleClick }: {
  group: FbGroup
  linkCount: number
  onRemove?: () => void
  onDoubleClick?: () => void
}) {
  return (
    <div
      className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg bg-card/80 border border-border/30 group cursor-grab hover:border-border/60 transition-colors"
      onDoubleClick={onDoubleClick}
    >
      <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${
        group.accessStatus === 'confirmed' ? 'bg-green-500' :
        group.accessStatus === 'denied' ? 'bg-red-500' : 'bg-zinc-500'
      }`} />
      <span className="text-[11px] truncate flex-1">{group.name}</span>
      {group.collection && (
        <Badge variant="outline" className="text-[8px] px-1 py-0 rounded border-border/40 shrink-0">{group.collection}</Badge>
      )}
      {linkCount > 1 && (
        <Badge variant="secondary" className="text-[8px] px-1 py-0 rounded-full shrink-0">{linkCount}x</Badge>
      )}
      {onRemove && (
        <button
          className="text-muted-foreground/50 hover:text-destructive transition-colors shrink-0"
          onClick={(e) => { e.stopPropagation(); e.preventDefault(); onRemove() }}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <X className="h-3 w-3" />
        </button>
      )}
    </div>
  )
}

// ─── MapaTab ────────────────────────────────────────────────

interface MapaTabProps {
  selectedProfileIds: string[]
}

export function MapaTab({ selectedProfileIds }: MapaTabProps) {
  const { t } = useLanguage()
  const hasSingleProfile = selectedProfileIds.length === 1
  const profileId = hasSingleProfile ? selectedProfileIds[0] : null

  const { data: groupsData } = useFbGroups(undefined, profileId ?? undefined)
  const { data: mapData } = useMapAccounts(profileId)
  const mapSyncGroups = useMapSyncGroups()

  const [search, setSearch] = useState('')
  const [importOpen, setImportOpen] = useState(false)
  const [activeGroup, setActiveGroup] = useState<FbGroup | null>(null)

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }))

  // Canvas pan (middle-click or click on empty space to drag-scroll)
  const scrollRef = useRef<HTMLDivElement>(null)
  const isPanning = useRef(false)
  const panStart = useRef({ x: 0, scrollLeft: 0 })

  const handlePanDown = useCallback((e: React.MouseEvent) => {
    const el = scrollRef.current
    if (!el) return
    // Only pan on middle-click or direct click on the scroll container/empty area
    if (e.button === 1 || (e.button === 0 && e.target === el)) {
      e.preventDefault()
      isPanning.current = true
      panStart.current = { x: e.clientX, scrollLeft: el.scrollLeft }
      el.style.cursor = 'grabbing'
    }
  }, [])

  const handlePanMove = useCallback((e: React.MouseEvent) => {
    if (!isPanning.current || !scrollRef.current) return
    const dx = e.clientX - panStart.current.x
    scrollRef.current.scrollLeft = panStart.current.scrollLeft - dx
  }, [])

  const handlePanUp = useCallback(() => {
    isPanning.current = false
    if (scrollRef.current) scrollRef.current.style.cursor = ''
  }, [])

  const allAccounts = mapData?.accounts ?? []
  const allGroups = groupsData?.groups ?? []

  // Hidden accounts (persisted in localStorage)
  const storageKey = `mapa-hidden-${profileId}`
  const [hiddenPageIds, setHiddenPageIds] = useState<Set<string>>(() => {
    try {
      const saved = localStorage.getItem(storageKey)
      return saved ? new Set(JSON.parse(saved)) : new Set()
    } catch { return new Set() }
  })

  const toggleHidden = useCallback((pageId: string) => {
    setHiddenPageIds(prev => {
      const next = new Set(prev)
      if (next.has(pageId)) next.delete(pageId)
      else next.add(pageId)
      localStorage.setItem(storageKey, JSON.stringify([...next]))
      return next
    })
  }, [storageKey])

  const showAll = useCallback(() => {
    setHiddenPageIds(new Set())
    localStorage.removeItem(storageKey)
  }, [storageKey])

  const accounts = allAccounts.filter(a => !hiddenPageIds.has(a.pageId))
  const hiddenCount = allAccounts.length - accounts.length

  // Build account→groups and find unassigned
  const { columns, unassigned } = useMemo(() => {
    const assignedGroupIds = new Set<string>()
    const cols = accounts.map((acc, i) => {
      const groups = acc.groupIds
        .map(id => allGroups.find(g => g.id === id))
        .filter(Boolean) as FbGroup[]
      groups.forEach(g => assignedGroupIds.add(g.id))
      return { account: acc, groups, color: COLUMN_COLORS[i % COLUMN_COLORS.length] }
    })
    const unassignedGroups = allGroups.filter(g => !assignedGroupIds.has(g.id))
    return { columns: cols, unassigned: unassignedGroups }
  }, [accounts, allGroups])

  // Count how many accounts each group is linked to
  const groupLinkCount = useMemo(() => {
    const counts: Record<string, number> = {}
    accounts.forEach(acc => {
      acc.groupIds.forEach(id => { counts[id] = (counts[id] ?? 0) + 1 })
    })
    return counts
  }, [accounts])

  const filterBySearch = (groups: FbGroup[]) => {
    if (!search) return groups
    const q = search.toLowerCase()
    return groups.filter(g => g.name.toLowerCase().includes(q) || g.collection?.toLowerCase().includes(q))
  }

  const handleRemoveLink = (acc: MapAccount, groupId: string) => {
    const newIds = acc.groupIds.filter(id => id !== groupId)
    mapSyncGroups.mutate(
      { page_id: acc.pageId, profile_id: profileId!, group_ids: newIds },
      { onError: (e) => toast.error(e.message) },
    )
  }

  const handleDragStart = (event: DragStartEvent) => {
    const group = event.active.data.current?.group as FbGroup | undefined
    if (group) setActiveGroup(group)
  }

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveGroup(null)
    const { active, over } = event
    if (!over) return

    const sourceColumnId = active.data.current?.sourceColumnId as string
    const targetColumnId = over.id as string
    const groupId = active.data.current?.group?.id as string

    if (!groupId || sourceColumnId === targetColumnId) return

    // Drag to "Sem página" = remove link from source account
    if (targetColumnId === '__unassigned__') {
      const sourceAcc = accounts.find(a => a.pageId === sourceColumnId)
      if (sourceAcc) {
        const newIds = sourceAcc.groupIds.filter(id => id !== groupId)
        mapSyncGroups.mutate(
          { page_id: sourceAcc.pageId, profile_id: profileId!, group_ids: newIds },
          { onError: (e) => toast.error(e.message) },
        )
      }
      return
    }

    // Find target account by pageId (column id = account.pageId)
    const targetAcc = accounts.find(a => a.pageId === targetColumnId)
    if (!targetAcc) return

    // Already linked?
    if (targetAcc.groupIds.includes(groupId)) return

    mapSyncGroups.mutate(
      { page_id: targetAcc.pageId, profile_id: profileId!, group_ids: [...targetAcc.groupIds, groupId] },
      { onError: (e) => toast.error(e.message) },
    )
  }

  if (!hasSingleProfile) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
        <AlertCircle className="h-8 w-8 mb-3" />
        <p className="text-sm font-medium">{t('Selecione exatamente 1 perfil na sidebar')}</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Action bar */}
      <div className="flex items-center gap-2 flex-wrap">
        <Button size="sm" className="rounded-lg gap-1.5 text-xs bg-emerald-600 hover:bg-emerald-700" onClick={() => setImportOpen(true)}>
          <Upload className="h-3 w-3" />{t('Import Lista')}
        </Button>
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          <Input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder={t('Buscar grupo...')}
            className="h-8 pl-8 text-xs rounded-lg border-border/40"
          />
          {search && (
            <button className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" onClick={() => setSearch('')}>
              <X className="h-3 w-3" />
            </button>
          )}
        </div>
        <span className="text-[10px] text-muted-foreground tabular-nums ml-auto">
          {accounts.length} {t('paginas')} · {allGroups.length} {t('grupos')}
          {hiddenCount > 0 && (
            <button onClick={showAll} className="ml-1 text-[10px] text-primary hover:underline">
              ({hiddenCount} {t('ocultas')} — {t('mostrar')})
            </button>
          )}
        </span>
      </div>

      {/* Kanban board */}
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <div
        ref={scrollRef}
        className="overflow-x-auto pb-4 cursor-grab select-none"
        onMouseDown={handlePanDown}
        onMouseMove={handlePanMove}
        onMouseUp={handlePanUp}
        onMouseLeave={handlePanUp}
      >
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
          <div className="flex gap-3" style={{ minWidth: 'max-content' }}>
            {/* Unassigned column */}
            <DroppableColumn
              id="__unassigned__"
              className="w-[220px] shrink-0 rounded-xl border border-dashed border-border/40 bg-muted/10 p-2"
            >
              <div className="flex items-center justify-between mb-2 px-1">
                <span className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider">{t('Sem pagina')}</span>
                <Badge variant="secondary" className="text-[10px] px-1.5 py-0 rounded-full">{filterBySearch(unassigned).length}</Badge>
              </div>
              <div className="space-y-1 max-h-[calc(100vh-320px)] overflow-y-auto">
                {filterBySearch(unassigned).map(g => (
                  <DraggableGroupCard
                    key={g.id}
                    group={g}
                    columnId="__unassigned__"
                    linkCount={0}
                    onDoubleClick={() => window.open(g.url, '_blank')}
                  />
                ))}
                {filterBySearch(unassigned).length === 0 && (
                  <p className="text-[10px] text-muted-foreground text-center py-4">{t('Nenhum')}</p>
                )}
              </div>
            </DroppableColumn>

            {/* Account columns */}
            {columns.map(({ account: acc, groups, color }) => (
              <DroppableColumn
                key={acc.pageId}
                id={acc.pageId}
                className="w-[220px] shrink-0 rounded-xl border p-2"
                style={{ background: color.bg, borderColor: color.border }}
              >
                <div className="flex items-center justify-between mb-2 px-1">
                  <span className="text-[11px] font-bold uppercase tracking-wider truncate" style={{ color: color.text }}>{acc.name}</span>
                  <Badge className="text-[10px] px-1.5 py-0 rounded-full border-0" style={{ background: color.border, color: color.text }}>{filterBySearch(groups).length}</Badge>
                  <button
                    onClick={() => toggleHidden(acc.pageId)}
                    className="text-muted-foreground/40 hover:text-muted-foreground transition-colors shrink-0"
                    title={t('Ocultar pagina')}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
                <div className="space-y-1 max-h-[calc(100vh-320px)] overflow-y-auto">
                  {filterBySearch(groups).map(g => (
                    <DraggableGroupCard
                      key={g.id}
                      group={g}
                      columnId={acc.pageId}
                      linkCount={groupLinkCount[g.id] ?? 1}
                      onRemove={() => handleRemoveLink(acc, g.id)}
                      onDoubleClick={() => window.open(g.url, '_blank')}
                    />
                  ))}
                  {filterBySearch(groups).length === 0 && (
                    <p className="text-[10px] text-muted-foreground text-center py-4">{t('Nenhum grupo')}</p>
                  )}
                </div>
              </DroppableColumn>
            ))}
          </div>

          <DragOverlay>
            {activeGroup && <GroupCard group={activeGroup} linkCount={0} />}
          </DragOverlay>
        </DndContext>
      </div>

      {/* Import dialog */}
      {importOpen && (
        <ImportStructuredDialog
          profileId={profileId!}
          accounts={accounts}
          onClose={() => setImportOpen(false)}
        />
      )}
    </div>
  )
}

// ─── Import Structured Dialog ───────────────────────────────

function ImportStructuredDialog({ profileId, accounts, onClose }: {
  profileId: string
  accounts: MapAccount[]
  onClose: () => void
}) {
  const { t } = useLanguage()
  const importStructured = useImportStructured()
  const [text, setText] = useState('')
  const [categories, setCategories] = useState<ParsedCategory[]>([])

  // Parse on text change with fuzzy match
  const parsed = useMemo(() => {
    const cats = parseStructuredList(text)
    return cats.map(cat => ({
      ...cat,
      pageId: cat.pageId ?? accounts.find(a => fuzzyMatch(a.name, cat.name))?.pageId ?? null,
    }))
  }, [text, accounts])

  const displayCats = categories.length > 0 ? categories : parsed
  const totalUrls = displayCats.reduce((sum, c) => sum + c.urls.length, 0)

  const handleImport = () => {
    if (displayCats.length === 0) return
    importStructured.mutate(
      {
        profile_id: profileId,
        categories: displayCats.map(c => ({ name: c.name, page_id: c.pageId, urls: c.urls })),
      },
      {
        onSuccess: (r) => {
          toast.success(`${r.created} criados, ${r.existing} existentes, ${r.links_created} vinculos`)
          onClose()
        },
        onError: (e) => toast.error(e.message),
      },
    )
  }

  const updatePageId = (index: number, pageId: string | null) => {
    const base = categories.length > 0 ? [...categories] : [...parsed]
    base[index] = { ...base[index], pageId }
    setCategories(base)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-card border border-border/40 rounded-2xl shadow-xl w-[700px] max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-border/30">
          <h3 className="text-sm font-bold">{t('Importar Lista Estruturada')}</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>
        </div>
        <div className="flex divide-x divide-border/30" style={{ minHeight: 300 }}>
          {/* Left: textarea */}
          <div className="flex-1 p-4">
            <p className="text-[10px] text-muted-foreground mb-2 uppercase font-semibold tracking-wider">{t('Cola a lista aqui')}</p>
            <textarea
              value={text}
              onChange={e => { setText(e.target.value); setCategories([]) }}
              placeholder={`NOME DA PAGINA\nhttps://facebook.com/groups/123\nhttps://facebook.com/groups/456\n\nOUTRA PAGINA\nhttps://facebook.com/groups/789`}
              className="w-full h-[280px] bg-muted/30 border border-border/30 rounded-lg p-3 text-xs font-mono resize-none focus:outline-none focus:border-primary/40"
            />
          </div>
          {/* Right: preview */}
          <div className="flex-1 p-4 overflow-y-auto">
            <p className="text-[10px] text-muted-foreground mb-2 uppercase font-semibold tracking-wider">{t('Preview')}</p>
            {displayCats.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-8">{t('Cole a lista para ver o preview')}</p>
            ) : (
              <div className="space-y-2">
                {displayCats.map((cat, i) => (
                  <div key={i} className="rounded-lg border border-border/30 bg-muted/10 p-2.5">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-bold">{cat.name}</span>
                      <Badge variant="secondary" className="text-[10px] px-1.5 py-0">{cat.urls.length} {t('grupos')}</Badge>
                      <span className="text-[10px] text-muted-foreground">&rarr;</span>
                      <select
                        value={cat.pageId ?? ''}
                        onChange={e => updatePageId(i, e.target.value || null)}
                        className="bg-muted/40 border border-border/30 rounded text-[11px] px-2 py-0.5 flex-1 min-w-[120px]"
                      >
                        <option value="">{t('Nao vincular')}</option>
                        {accounts.map(a => (
                          <option key={a.pageId} value={a.pageId}>{a.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                ))}
                <div className="text-[10px] text-muted-foreground tabular-nums pt-1">
                  {displayCats.length} {t('categorias')} · {totalUrls} {t('grupos')}
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="px-5 py-3 border-t border-border/30 flex justify-end gap-2">
          <Button variant="outline" size="sm" className="rounded-lg text-xs" onClick={onClose}>{t('Cancelar')}</Button>
          <Button
            size="sm"
            className="rounded-lg text-xs bg-emerald-600 hover:bg-emerald-700"
            disabled={displayCats.length === 0 || importStructured.isPending}
            onClick={handleImport}
          >
            {importStructured.isPending ? t('Importando...') : t(`Importar ${totalUrls} Grupos`)}
          </Button>
        </div>
      </div>
    </div>
  )
}
